import React, { useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill, Btn } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead } from '../platform/_ui';
import { RequireCapability } from './auth/RequireCapability.jsx';
import { DeniedState } from './auth/DeniedState.jsx';
import { ORG } from './auth/capabilities.js';
import { useOrganization } from './OrganizationRuntimeContext.jsx';
import * as api from '../api/org-search.api.js';

/* Inline keyframe — avoids dependency on OrgIAM.css being loaded first */
const SPIN_KF = `@keyframes nx-search-spin { to { transform: rotate(360deg); } }`;

/* ── Resource Registry ──────────────────────────────────────────────── */
/* Single source of truth. Every searchable type and every popular page
   is defined here. All other data structures are derived from it.
   Rules: route must exist, nav must exist, page must exist.            */

const RESOURCE_REGISTRY = [
  /* People & Access */
  { type: 'member',        icon: 'User',          tone: 'blue',   label: 'Member',          filterLabel: 'Members',            placeholder: 'Search members…',               group: 'People & Access', groupIcon: 'Users',         path: 'members',   popular: true,  popularIcon: 'Users',         popularDesc: 'Manage member roles and access'       },
  { type: 'workspace',     icon: 'LayoutGrid',    tone: 'purple', label: 'Workspace',       filterLabel: 'Workspaces',         placeholder: 'Search workspaces…',            group: 'People & Access', groupIcon: 'Users',         path: 'workspaces',popular: true,  popularIcon: 'LayoutGrid',    popularDesc: 'Browse and configure workspaces'      },
  { type: 'team',          icon: 'UsersRound',    tone: 'green',  label: 'Team',            filterLabel: 'Teams',              placeholder: 'Search teams…',                 group: 'People & Access', groupIcon: 'Users',         path: 'teams',     popular: true,  popularIcon: 'UsersRound',    popularDesc: 'Create and manage organization teams' },
  { type: 'role',          icon: 'Shield',        tone: 'amber',  label: 'Role',            filterLabel: 'Custom Roles',       placeholder: 'Search roles…',                 group: 'People & Access', groupIcon: 'Users',         path: 'iam',       popular: false },
  /* Developer */
  { type: 'apikey',        icon: 'Key',           tone: 'purple', label: 'API Key',         filterLabel: 'API Keys',           placeholder: 'Search API keys…',              group: 'Developer',       groupIcon: 'Code',          path: 'developer', popular: false },
  { type: 'webhook',       icon: 'Webhook',       tone: 'blue',   label: 'Webhook',         filterLabel: 'Webhooks',           placeholder: 'Search webhooks…',              group: 'Developer',       groupIcon: 'Code',          path: 'developer', popular: false },
  { type: 'oauth',         icon: 'Globe',         tone: 'green',  label: 'OAuth App',       filterLabel: 'OAuth Apps',         placeholder: 'Search OAuth applications…',    group: 'Developer',       groupIcon: 'Code',          path: 'developer', popular: false },
  /* AI Platform */
  { type: 'agent',         icon: 'Bot',           tone: 'purple', label: 'AI Agent',        filterLabel: 'AI Agents',          placeholder: 'Search AI agents…',             group: 'AI Platform',     groupIcon: 'Bot',           path: 'ai',        popular: false },
  { type: 'knowledgebase', icon: 'BookOpen',      tone: 'blue',   label: 'Knowledge Base',  filterLabel: 'Knowledge Bases',    placeholder: 'Search knowledge bases…',       group: 'AI Platform',     groupIcon: 'Bot',           path: 'ai',        popular: false },
  { type: 'prompt',        icon: 'Sparkles',      tone: 'amber',  label: 'Prompt',          filterLabel: 'Prompt Templates',   placeholder: 'Search prompts…',               group: 'AI Platform',     groupIcon: 'Bot',           path: 'ai',        popular: false },
  /* Communication */
  { type: 'channel',       icon: 'MessageSquare', tone: 'blue',   label: 'Channel',         filterLabel: 'Channels',           placeholder: 'Search channels…',              group: 'Communication',   groupIcon: 'MessageSquare', path: 'channels',  popular: true,  popularIcon: 'MessageSquare', popularDesc: 'Messaging channel configuration'       },
  { type: 'template',      icon: 'Layout',        tone: 'green',  label: 'Template',        filterLabel: 'Workspace Templates',placeholder: 'Search workspace templates…',   group: 'Communication',   groupIcon: 'MessageSquare', path: 'workspaces',popular: false },
  /* Governance */
  { type: 'license',       icon: 'CreditCard',    tone: 'amber',  label: 'License',         filterLabel: 'Licenses',           placeholder: 'Search licenses…',              group: 'Governance',      groupIcon: 'ClipboardList', path: 'licenses',  popular: true,  popularIcon: 'CreditCard',    popularDesc: 'View license allocations and usage'   },
  { type: 'audit',         icon: 'ClipboardList', tone: 'gray',   label: 'Audit Log',       filterLabel: 'Audit Logs',         placeholder: 'Search audit logs…',            group: 'Governance',      groupIcon: 'ClipboardList', path: 'audit',     popular: true,  popularIcon: 'ClipboardList', popularDesc: 'Security events and activity records' },
  { type: 'timeline',      icon: 'Activity',      tone: 'blue',   label: 'Timeline',        filterLabel: 'Activity Timeline',  placeholder: 'Search activity timeline…',     group: 'Governance',      groupIcon: 'ClipboardList', path: 'timeline',  popular: true,  popularIcon: 'Activity',      popularDesc: 'Organization activity history'        },
  { type: 'ipallowlist',   icon: 'ShieldCheck',   tone: 'green',  label: 'IP Rule',         filterLabel: 'IP Allowlist',       placeholder: 'Search IP rules…',              group: 'Governance',      groupIcon: 'ClipboardList', path: 'security',  popular: false },
  /* Configuration */
  { type: 'setting',       icon: 'Settings',      tone: 'gray',   label: 'Setting',         filterLabel: 'Settings',           placeholder: 'Search organization settings…', group: 'Configuration',   groupIcon: 'Settings',      path: 'settings',  popular: false },
  /* Nav-only popular pages (no backend search type) */
  { type: null, icon: 'Bot',        label: 'AI Platform',     popular: true, popularIcon: 'Bot',        popularDesc: 'Agents, knowledge bases and prompts',    path: 'ai'            },
  { type: null, icon: 'HardDrive',  label: 'Storage',         popular: true, popularIcon: 'HardDrive',  popularDesc: 'File storage quotas and usage',           path: 'storage'       },
  { type: null, icon: 'Code',       label: 'Developer Center',popular: true, popularIcon: 'Code',       popularDesc: 'API keys, webhooks and OAuth apps',      path: 'developer'     },
  { type: null, icon: 'Palette',    label: 'Branding',        popular: true, popularIcon: 'Palette',    popularDesc: 'Logo, colors and white label settings',   path: 'branding'      },
  { type: null, icon: 'CreditCard', label: 'Billing',         popular: true, popularIcon: 'CreditCard', popularDesc: 'Subscription, licenses and invoices',     path: 'billing'       },
  { type: null, icon: 'Bell',       label: 'Notifications',   popular: true, popularIcon: 'Bell',       popularDesc: 'Alert preferences and delivery channels', path: 'notifications' },
  { type: null, icon: 'Plug',       label: 'Integrations',    popular: true, popularIcon: 'Plug',       popularDesc: 'Third-party integration catalog',         path: 'integrations'  },
];

/* ── Derived data (generated from registry — do not edit directly) ── */

const SEARCH_TYPES     = RESOURCE_REGISTRY.filter(r => r.type);
const POPULAR_RESOURCES = RESOURCE_REGISTRY.filter(r => r.popular);

const TYPE_META = Object.fromEntries(
  SEARCH_TYPES.map(r => [r.type, { icon: r.icon, tone: r.tone, label: r.label }])
);

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  ...SEARCH_TYPES.map(r => ({ value: r.type, label: r.filterLabel })),
];

const PLACEHOLDER_MAP = Object.fromEntries(
  SEARCH_TYPES.map(r => [r.type, r.placeholder])
);

/* Deduplicated ordered list of browse categories */
const BROWSE_CATEGORIES = (() => {
  const seen = new Map();
  for (const r of SEARCH_TYPES) {
    if (!r.group) continue;
    if (!seen.has(r.group)) seen.set(r.group, { label: r.group, icon: r.groupIcon, types: [] });
    seen.get(r.group).types.push(r.type);
  }
  return [...seen.values()];
})();

/* ── Helpers ────────────────────────────────────────────────────────── */

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function statusTone(status) {
  if (status === 'INACTIVE' || status === 'EXPIRED') return 'amber';
  if (status === 'SUSPENDED') return 'red';
  return 'gray';
}

/* ── Result Row ─────────────────────────────────────────────────────── */

function ResultRow({ item, onSelect }) {
  const meta = TYPE_META[item.type] ?? { icon: 'File', tone: 'gray', label: item.type };
  const date = fmtDate(item.updatedAt);
  const showStatus = item.status && item.status !== 'ACTIVE' && item.status !== 'INFO';

  return (
    <button
      type="button"
      className="nx-search-result-row"
      onClick={() => onSelect(item)}
      aria-label={`${item.title} — ${meta.label}`}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <NexusIcon name={meta.icon} size={15} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title}
        </div>
        {item.subtitle && (
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.subtitle}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {showStatus && <Pill tone={statusTone(item.status)}>{item.status}</Pill>}
        <Pill tone={meta.tone}>{meta.label}</Pill>
        {date && <span style={{ fontSize: 11, color: 'var(--nx-text-faint)', minWidth: 68, textAlign: 'right' }}>{date}</span>}
        <NexusIcon name="ChevronRight" size={14} style={{ color: 'var(--nx-text-faint)' }} />
      </div>
    </button>
  );
}

/* ── Group Card ─────────────────────────────────────────────────────── */

function GroupCard({ group, onSelect }) {
  const meta = TYPE_META[group.type] ?? { icon: 'File', tone: 'gray', label: group.type };
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <NexusIcon name={meta.icon} size={12} />
        {group.label}
        <span style={{ fontWeight: 600, color: 'var(--nx-text-faint)' }}>({group.items.length})</span>
      </div>
      <div className="nx-cc-chart-card" style={{ padding: 0, overflow: 'hidden' }}>
        {group.items.map((r, i) => (
          <div key={r.id + String(i)} style={{ borderBottom: i < group.items.length - 1 ? '1px solid var(--nx-border)' : 'none' }}>
            <ResultRow item={r} onSelect={onSelect} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Result Skeleton ────────────────────────────────────────────────── */

function ResultSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {[0, 1, 2].map(i => (
        <div key={i}>
          <div className="nx-cc-skeleton" style={{ height: 14, width: 110, borderRadius: 4, marginBottom: 10 }} />
          <div className="nx-cc-chart-card" style={{ padding: 0, overflow: 'hidden' }}>
            {[0, 1, 2, 3].map(j => (
              <div key={j} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px',
                borderBottom: j < 3 ? '1px solid var(--nx-border)' : 'none',
              }}>
                <div className="nx-cc-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="nx-cc-skeleton" style={{ height: 13, width: '52%', borderRadius: 4, marginBottom: 7 }} />
                  <div className="nx-cc-skeleton" style={{ height: 11, width: '32%', borderRadius: 4 }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div className="nx-cc-skeleton" style={{ height: 20, width: 58, borderRadius: 20 }} />
                  <div className="nx-cc-skeleton" style={{ height: 20, width: 74, borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Category Chip ──────────────────────────────────────────────────── */

function CategoryChip({ type, icon, label, isActive, onSelect }) {
  return (
    <button
      type="button"
      className="nx-category-chip"
      aria-pressed={isActive}
      onClick={() => onSelect(type)}
      style={{
        border: `1px solid ${isActive ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
        background: isActive ? 'var(--nx-accent)' : 'var(--nx-surface-alt)',
        fontWeight: isActive ? 600 : 500,
        color: isActive ? '#fff' : 'var(--nx-text)',
      }}
      aria-label={isActive ? `Remove ${label} filter` : `Filter by ${label}`}
    >
      <NexusIcon name={icon} size={11} />
      {label}
    </button>
  );
}

/* ── Landing Content ────────────────────────────────────────────────── */

function LandingContent({ orgId, navigate, onTypeSearch, activeType }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* Popular Resources */}
      <section aria-label="Popular resources">
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14,
        }}>
          Popular Resources
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
          gap: 10,
        }}>
          {POPULAR_RESOURCES.map(r => (
            <button
              key={r.path + r.label}
              type="button"
              className="nx-resource-card"
              onClick={() => navigate(`/organization/${orgId}/${r.path}`)}
              aria-label={`Navigate to ${r.label}`}
            >
              <div style={{
                width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <NexusIcon name={r.popularIcon} size={15} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', marginBottom: 4 }}>
                  {r.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                  {r.popularDesc}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Browse by Category */}
      <section aria-label="Browse by category">
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4,
        }}>
          Browse by Category
        </div>
        <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
          Select a resource type to narrow your search, then enter a keyword.
        </div>
        <div className="nx-cc-chart-card" style={{ padding: 0 }}>
          {/* All Resources row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '12px 20px', flexWrap: 'wrap',
            borderBottom: '1px solid var(--nx-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148, flexShrink: 0 }}>
              <NexusIcon name="Layers" size={13} style={{ color: 'var(--nx-text-muted)' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>All Resources</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <CategoryChip
                type=""
                icon="LayoutGrid"
                label="All Resources"
                isActive={!activeType}
                onSelect={onTypeSearch}
              />
            </div>
          </div>
          {BROWSE_CATEGORIES.map((cat, ci) => (
            <div
              key={cat.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '12px 20px', flexWrap: 'wrap',
                borderBottom: ci < BROWSE_CATEGORIES.length - 1 ? '1px solid var(--nx-border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148, flexShrink: 0 }}>
                <NexusIcon name={cat.icon} size={13} style={{ color: 'var(--nx-text-muted)' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>{cat.label}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cat.types.map(t => {
                  const m = TYPE_META[t];
                  return (
                    <CategoryChip
                      key={t}
                      type={t}
                      icon={m.icon}
                      label={m.label}
                      isActive={activeType === t}
                      onSelect={onTypeSearch}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────── */

function OrgSearchInner() {
  const { orgId }   = useParams();
  const navigate    = useNavigate();
  const toast       = useToast();
  const org         = useOrganization();

  const [query,         setQuery]         = useState('');
  const [typeFilter,    setTypeFilter]    = useState('');
  const [groups,        setGroups]        = useState([]);
  const [total,         setTotal]         = useState(0);
  const [cursor,        setCursor]        = useState(null);
  const [hasMore,       setHasMore]       = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [error,         setError]         = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  const debounceRef = useRef(null);
  const inputRef    = useRef(null);

  const doSearch = useCallback(async (q, type, append = false, cur = null) => {
    if (!q.trim()) {
      setGroups([]); setTotal(0); setCursor(null); setHasMore(false); setError(null);
      return;
    }
    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError(null);
      const r = await api.search(orgId, { q, type: type || undefined, limit: 20, cursor: cur || undefined });
      const incoming = r.groups ?? [];
      if (append) {
        setGroups(prev => {
          const merged = [...prev];
          for (const g of incoming) {
            const idx = merged.findIndex(m => m.type === g.type);
            if (idx >= 0) merged[idx] = { ...merged[idx], items: [...merged[idx].items, ...g.items] };
            else merged.push(g);
          }
          return merged;
        });
      } else {
        setGroups(incoming);
      }
      setTotal(r.total ?? 0);
      setCursor(r.cursor ?? null);
      setHasMore(r.hasMore ?? false);
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Request failed';
      setError('Search failed. Please try again.');
      if (!append) { setGroups([]); setTotal(0); }
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Search failed', sub: detail });
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [orgId, toast]);

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v, typeFilter), 300);
  };

  const handleTypeChange = (e) => {
    const t = e.target.value;
    setTypeFilter(t);
    if (query.trim()) doSearch(query, t);
  };

  const handleLoadMore = () => {
    if (cursor && !loadingMore) doSearch(query, typeFilter, true, cursor);
  };

  const handleSelect = (item) => {
    if ((item.type === 'member' || item.type === 'workspace') && item.id) {
      navigate(`/organization/${orgId}/${item.path}?id=${encodeURIComponent(item.id)}`);
    } else {
      navigate(`/organization/${orgId}/${item.path}`);
    }
  };

  const handleClear = () => {
    setQuery(''); setTypeFilter(''); setGroups([]); setTotal(0);
    setCursor(null); setHasMore(false); setError(null);
    inputRef.current?.focus();
  };

  /* Called from Browse by Category chips — toggles type filter and focuses input */
  const handleTypeSearch = (type) => {
    setTypeFilter(typeFilter === type ? '' : type);
    inputRef.current?.focus();
  };

  const plural           = total !== 1 ? 's' : '';
  const filterLabel      = typeFilter ? ` in ${TYPE_META[typeFilter]?.label ?? typeFilter}` : '';
  const resultCount      = total > 0 ? `${total} result${plural}${filterLabel}` : null;
  const searchPlaceholder = PLACEHOLDER_MAP[typeFilter] ?? 'Search organization resources…';

  const term        = query.trim();
  const hasResults  = !loading && !error && term && groups.length > 0;
  const hasNoResults = !loading && !error && term && groups.length === 0;

  if (!org) return null;

  return (
    <>
      <style>{SPIN_KF}</style>
      <div className="nx-cc-page">

        <PageHead
          title="Search"
          sub={`Find organization resources in ${org.name}`}
        />

        {/* ── Hero Search Bar ─────────────────────────────────────── */}
        <div style={{ marginBottom: 6 }}>
          <div
            role="search"
            style={{
              display: 'flex', alignItems: 'stretch',
              border: `1.5px solid ${searchFocused ? 'var(--nx-accent)' : 'var(--nx-border)'}`,
              borderRadius: 10, overflow: 'hidden',
              background: 'var(--nx-surface)',
              boxShadow: searchFocused
                ? '0 0 0 3px var(--nx-accent-tint), 0 2px 8px rgba(0,0,0,0.08)'
                : '0 1px 4px rgba(0,0,0,0.06)',
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
            onFocusCapture={() => setSearchFocused(true)}
            onBlurCapture={() => setSearchFocused(false)}
          >
            {/* Search / Loading icon */}
            <div style={{
              display: 'flex', alignItems: 'center',
              paddingLeft: 18, paddingRight: 2, flexShrink: 0,
              color: loading ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
            }}>
              <NexusIcon
                name={loading ? 'Loader2' : 'Search'}
                size={20}
                style={loading ? { animation: 'nx-search-spin 0.8s linear infinite' } : undefined}
              />
            </div>

            {/* Input wrapper */}
            <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
              <input
                ref={inputRef}
                style={{
                  width: '100%', height: 54, border: 'none', background: 'transparent',
                  outline: 'none', paddingLeft: 12, paddingRight: query ? 40 : 12,
                  fontSize: 15, color: 'var(--nx-text)',
                }}
                placeholder={searchPlaceholder}
                value={query}
                onChange={handleQueryChange}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                aria-label="Search organization resources"
              />
              {query && !loading && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{
                    position: 'absolute', right: 10,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--nx-text-muted)', padding: 4,
                    display: 'flex', alignItems: 'center', borderRadius: 4,
                  }}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <NexusIcon name="X" size={15} />
                </button>
              )}
            </div>

            {/* Divider */}
            <div style={{ width: 1, background: 'var(--nx-border)', flexShrink: 0, margin: '12px 0' }} />

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={handleTypeChange}
              style={{
                height: 54, border: 'none', background: 'transparent', outline: 'none',
                paddingLeft: 14, paddingRight: 14, minWidth: 172, flexShrink: 0,
                fontSize: 13, color: 'var(--nx-text)', cursor: 'pointer',
              }}
              aria-label="Filter by resource type"
            >
              {TYPE_FILTER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Result summary line */}
          {term && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--nx-text-muted)', minHeight: 18, paddingLeft: 2 }}>
              {(() => {
                if (loading) return 'Searching…';
                if (error)   return error;
                return resultCount ?? `No results for "${query}"`;
              })()}
            </div>
          )}
        </div>

        {/* ── Content Area ────────────────────────────────────────── */}
        <div style={{ marginTop: 24 }}>

          {/* Loading skeleton */}
          {loading && <ResultSkeleton />}

          {/* Error state */}
          {!loading && error && (
            <div className="nx-empty">
              <NexusIcon name="AlertCircle" size={24} />
              <span>{error}</span>
              <Btn variant="outline" size="sm" onClick={() => doSearch(query, typeFilter)}>Retry</Btn>
            </div>
          )}

          {/* Search results */}
          {hasResults && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {groups.map(g => <GroupCard key={g.type} group={g} onSelect={handleSelect} />)}
              {hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
                  <Btn
                    variant="outline" size="sm"
                    icon={loadingMore ? 'Loader2' : 'ChevronDown'}
                    disabled={loadingMore}
                    onClick={handleLoadMore}
                  >
                    {loadingMore ? 'Loading…' : 'Load more results'}
                  </Btn>
                </div>
              )}
            </div>
          )}

          {/* Empty search state */}
          {hasNoResults && (
            <div style={{ padding: '52px 24px', textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'var(--nx-surface-alt)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
                border: '1.5px solid var(--nx-border)',
              }}>
                <NexusIcon name="SearchX" size={26} style={{ color: 'var(--nx-text-muted)' }} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--nx-text)', marginBottom: 8 }}>
                No results found
              </div>
              <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', maxWidth: 420, margin: '0 auto 20px', lineHeight: 1.6 }}>
                No organization resources matched{' '}
                <strong style={{ color: 'var(--nx-text)' }}>"{query}"</strong>
                {typeFilter ? ` in ${TYPE_META[typeFilter]?.label ?? typeFilter}` : ''}.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 6, marginBottom: 20 }}>
                <Pill tone="gray">Check your spelling</Pill>
                <Pill tone="gray">Use fewer keywords</Pill>
                {typeFilter && <Pill tone="gray">Try searching all types</Pill>}
              </div>
              {typeFilter && (
                <Btn variant="outline" size="sm" onClick={() => { setTypeFilter(''); doSearch(query, ''); }}>
                  Remove type filter
                </Btn>
              )}
            </div>
          )}

          {/* Landing state */}
          {!term && (
            <LandingContent orgId={orgId} navigate={navigate} onTypeSearch={handleTypeSearch} activeType={typeFilter} />
          )}

        </div>
      </div>
    </>
  );
}

export default function OrgSearch() {
  return (
    <RequireCapability cap={ORG.SEARCH_USE} denied={<DeniedState reason="insufficient_permission" />}>
      <OrgSearchInner />
    </RequireCapability>
  );
}
